import React, { useCallback } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Label } from 'Components/ui/label';
import { flatSelectStyles } from './flatSelectStyles';
import axiosInstance from '../../../redux/api';
import { useProductSearch } from '../Catalouge/Product/form/useProductSearch';

/**
 * "When tapped, open": what a customer lands on after tapping the push.
 * The server checks the choice is live in the store before sending, and
 * says why when it is not.
 */
export const LINK_TYPES = [
  { value: '', label: 'Just open the app' },
  { value: 'product', label: 'A product' },
  { value: 'brand', label: 'A brand' },
  { value: 'category', label: 'A category' },
  { value: 'offer', label: 'An offer' },
];

// The admin list each non-product target is searched in, and where its rows are.
const SOURCES = {
  brand: { url: 'admin/brands', params: {}, rows: (data) => data },
  category: { url: 'admin/categories', params: { type: 1 }, rows: (data) => data },
  offer: { url: 'admin/offers', params: {}, rows: (data) => data?.offers },
};

const toOption = (row) => ({ value: row.id, label: row.name || row.title || `#${row.id}` });

const PushLinkPicker = ({ linkType, linkItem, onTypeChange, onItemChange, disabled, error }) => {
  const { loadOptions: loadProducts } = useProductSearch();

  // Switched-off rows are left out: the server would refuse them anyway.
  // A search with no match answers 422, which here just means "none".
  const loadRows = useCallback(
    async (input) => {
      const source = SOURCES[linkType];
      try {
        const response = await axiosInstance.get(source.url, {
          params: { page: 1, per_page: 50, search_key: input || '', status: '', ...source.params },
        });
        const rows = source.rows(response?.data?.response);
        return (Array.isArray(rows) ? rows : [])
          .filter((row) => row.status === undefined || Number(row.status) === 1)
          .map(toOption);
      } catch {
        return [];
      }
    },
    [linkType],
  );

  const typeLabel = LINK_TYPES.find((option) => option.value === linkType)?.label.replace(/^(A|An) /, '');

  return (
    <div className='space-y-2'>
      <Label htmlFor='push-link-type'>Open</Label>
      <Select
        inputId='push-link-type'
        styles={flatSelectStyles}
        options={LINK_TYPES}
        value={LINK_TYPES.find((option) => option.value === linkType)}
        isDisabled={disabled}
        onChange={(selected) => onTypeChange(selected.value)}
      />
      {linkType && (
        <div className='space-y-1.5'>
          <AsyncSelect
            // A new type is a new list: remount so cached options never mix.
            key={linkType}
            styles={flatSelectStyles}
            cacheOptions
            defaultOptions={linkType !== 'product'}
            loadOptions={linkType === 'product' ? loadProducts : loadRows}
            value={linkItem}
            onChange={onItemChange}
            isDisabled={disabled}
            isClearable
            placeholder={linkType === 'product' ? 'Search by name or barcode…' : `Search ${typeLabel}…`}
            noOptionsMessage={({ inputValue }) =>
              linkType === 'product' && !inputValue ? 'Type to search products' : 'Nothing found'
            }
          />
          {error && <p className='text-xs text-destructive'>{error}</p>}
        </div>
      )}
    </div>
  );
};

export default PushLinkPicker;
